import { FC } from 'react';
import {
  Box,
  InputBase,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { BorderedSelect } from '@components';
import { useTrans } from '@libs/i18n';
import { FunnelIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';

import {
  type HubDisplayStatus,
  STATUS_META,
} from './documentStatusMeta';
import type { StatusCounts } from './DocumentStatusStrip';

export interface CategoryOption {
  key: string;
  name: string;
}

interface Props {
  total: number;
  counts: StatusCounts;
  activeStatus: HubDisplayStatus | null;
  onStatusChange: (status: HubDisplayStatus | null) => void;
  categories: CategoryOption[];
  activeCategory: string | null;
  onCategoryChange: (key: string | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
}

/**
 * Canonical filter set, always rendered in this order (even at a zero count) so
 * the filter row is stable and matches the design.
 */
const TAB_ORDER: HubDisplayStatus[] = [
  'pending_review',
  'extracting',
  'done',
  'failed',
  'irrelevant',
];

const Tab: FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = observer(({ label, count, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.625,
      flex: 'none',
      px: 1.5,
      py: 0.625,
      borderRadius: 999,
      cursor: 'pointer',
      fontFamily: 'inherit',
      // Inactive tabs carry a light surface + border so they read as clickable
      // chips rather than plain text; the active tab is a solid dark pill.
      border: 1,
      borderColor: active ? 'text.primary' : 'grey.300',
      backgroundColor: active ? 'text.primary' : 'grey.100',
      transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
        backgroundColor: active ? 'text.primary' : 'grey.200',
        borderColor: active ? 'text.primary' : 'grey.400',
        boxShadow: active ? 'none' : 1,
      },
      '&:active': { boxShadow: 'none' },
    }}
  >
    <Typography
      variant="textXS_semiBold"
      color={active ? 'background.paper' : 'text.secondary'}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {label}
    </Typography>
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        px: 0.5,
        borderRadius: 999,
        typography: 'textXS_semiBold',
        backgroundColor: active ? 'rgba(255, 255, 255, 0.24)' : 'grey.200',
        color: active ? 'background.paper' : 'text.tertiary',
      }}
    >
      {count}
    </Box>
  </Box>
));

/** Status filter tabs + category dropdown + filename search. */
export const DocumentFilters: FC<Props> = observer(
  ({
    total,
    counts,
    activeStatus,
    onStatusChange,
    categories,
    activeCategory,
    onCategoryChange,
    search,
    onSearchChange,
  }) => {
    const { t } = useTrans();
    const theme = useTheme();

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          p: 1.5,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'grey.300',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tab
            label={t('app.documentHub.status.all')}
            count={total}
            active={activeStatus === null}
            onClick={() => onStatusChange(null)}
          />
          {TAB_ORDER.map((status) => (
            <Tab
              key={status}
              label={t(STATUS_META[status].labelKey)}
              count={counts[status] ?? 0}
              active={activeStatus === status}
              onClick={() =>
                onStatusChange(activeStatus === status ? null : status)
              }
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <BorderedSelect
            value={activeCategory ?? ''}
            onChange={(e) => onCategoryChange((e.target.value as string) || null)}
            displayEmpty
            minWidth={120}
            textVariant="textXS_semiBold"
            startIcon={<FunnelIcon size={14} color={theme.palette.text.tertiary} />}
          >
            <MenuItem value="" sx={{ typography: 'textXS_semiBold' }}>
              {t('app.documentHub.allCategories')}
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.key} value={c.key} sx={{ typography: 'textXS_semiBold' }}>
                {c.name}
              </MenuItem>
            ))}
          </BorderedSelect>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.625,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'grey.300',
              minWidth: 200,
            }}
          >
            <MagnifyingGlassIcon size={14} color={theme.palette.text.tertiary} />
            <InputBase
              fullWidth
              value={search}
              placeholder={t('app.documentHub.search')}
              onChange={(e) => onSearchChange(e.target.value)}
              sx={{ typography: 'textXS_regular', color: 'text.primary' }}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);
